import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { affiliateService } from '../../services/affiliateService';

interface Plan {
  id: string;
  program_id: string;
  name: string;
  commission_type: 'percentage' | 'fixed';
  default_rate: number;
  tiers_enabled?: boolean;
  is_active: boolean;
  created_at?: string;
}

interface PlansManagementProps {
  programId?: string;
}

const PlansManagement: React.FC<PlansManagementProps> = ({ programId }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [form, setForm] = useState<Partial<Plan>>({ name: '', commission_type: 'percentage', default_rate: 10, tiers_enabled: false, is_active: true });

  const fetchPlans = async () => {
    if (!programId) {
      setPlans([]);
      setError('No program ID provided');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await affiliateService.getPlans(programId);
      setPlans(Array.isArray(data) ? data as any : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load plans');
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!programId) {
        toast.error('No program ID provided');
        return;
      }
      if (!form.name || !form.commission_type || form.default_rate == null) {
        toast.error('Please fill in all required fields');
        return;
      }
      await affiliateService.createPlan({
        program_id: programId,
        name: String(form.name),
        commission_type: form.commission_type as 'percentage' | 'fixed',
        default_rate: Number(form.default_rate),
        tiers_enabled: Boolean(form.tiers_enabled),
      });
      toast.success('Plan created');
      setForm({ name: '', commission_type: 'percentage', default_rate: 10, tiers_enabled: false, is_active: true });
      fetchPlans();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create plan');
    }
  };

  const handleToggleActive = async (plan: Plan) => {
    try {
      await affiliateService.updatePlan(plan.id, { is_active: !plan.is_active });
      fetchPlans();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update plan');
    }
  };

  const handleDelete = async (plan: Plan) => {
    try {
      await affiliateService.deletePlan(plan.id);
      toast.success('Plan deleted');
      fetchPlans();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete plan');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Commission Plans</h2>
        <p className="text-sm text-gray-500">Create and manage commission plans for your program.</p>
      </div>

      <form onSubmit={handleCreate} className="bg-white rounded-lg shadow p-4 mb-6 grid grid-cols-1 md:grid-cols-6 gap-4">
        <input
          type="text"
          className="border rounded px-3 py-2"
          placeholder="Plan name"
          value={form.name || ''}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          required
        />
        <select
          className="border rounded px-3 py-2"
          value={form.commission_type || 'percentage'}
          onChange={(e) => setForm((f) => ({ ...f, commission_type: e.target.value as any }))}
        >
          <option value="percentage">Percentage</option>
          <option value="fixed">Fixed</option>
        </select>
        <input
          type="number"
          className="border rounded px-3 py-2"
          placeholder={form.commission_type === 'fixed' ? 'Amount' : 'Percent'}
          value={form.default_rate ?? 10}
          onChange={(e) => setForm((f) => ({ ...f, default_rate: Number(e.target.value) }))}
          min={0}
          step={form.commission_type === 'fixed' ? 0.01 : 1}
          required
        />
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(form.tiers_enabled)}
            onChange={(e) => setForm((f) => ({ ...f, tiers_enabled: e.target.checked }))}
          />
          Tiers Enabled
        </label>
        <div className="flex items-center text-sm text-gray-500">Program: {programId || '-'}</div>
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-3 rounded"
        >
          Create Plan
        </button>
      </form>

      {loading ? (
        <p className="text-gray-500">Loading plans...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : plans.length === 0 ? (
        <p className="text-gray-500">No plans yet. Create your first plan above.</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-left text-sm text-gray-600">
                <th className="py-3 px-3">Name</th>
                <th className="py-3 px-3">Type</th>
                <th className="py-3 px-3">Default Rate</th>
                <th className="py-3 px-3">Tiers</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 text-sm">
                  <td className="py-3 px-3">{p.name}</td>
                  <td className="py-3 px-3">{p.commission_type}</td>
                  <td className="py-3 px-3">{p.commission_type === 'fixed' ? `$${p.default_rate.toFixed(2)}` : `${p.default_rate}%`}</td>
                  <td className="py-3 px-3">{p.tiers_enabled ? 'Enabled' : 'Disabled'}</td>
                  <td className="py-3 px-3"><span className={p.is_active ? 'text-green-600' : 'text-gray-500'}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="py-3 px-3 flex gap-2">
                    <button className="text-blue-600 hover:underline" onClick={() => handleToggleActive(p)}>{p.is_active ? 'Disable' : 'Enable'}</button>
                    <button className="text-red-600 hover:underline" onClick={() => handleDelete(p)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlansManagement;
